/**
 * Bandeau d'état d'un référentiel, en tête de campagne.
 *
 * Le référentiel se construit PENDANT la campagne (référents invités, relancés,
 * contributions tardives) : ce qui bloque ou fait encore bouger le matching
 * doit se voir là où l'on lit les résultats, pas seulement dans l'onglet
 * Référentiels. Le bandeau ne s'affiche que s'il reste quelque chose à faire.
 */
import { useQuery } from "convex/react";
import { Compass } from "lucide-react";
import { Button } from "@repo/ui";
import { pluriel } from "@repo/utils";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { LIBELLE_CIBLE, type CibleReferentiel } from "../ongletsCampagne";

type Props = {
  campagneId: Id<"campagnes">;
  cible: CibleReferentiel;
  /** Le référentiel existe-t-il sur la campagne ? */
  configure: boolean;
  onGerer: () => void;
};

export function BandeauReferentiel({
  campagneId,
  cible,
  configure,
  onGerer,
}: Props) {
  const suivi = useQuery(
    api.referentiel.suivi.obtenirSuiviReferents,
    configure ? { campagneId, cible } : "skip",
  );

  let message: string | null = null;
  let bloquant = false;
  if (!configure) {
    message = "Référentiel non configuré : pas de matching possible.";
    bloquant = true;
  } else if (suivi) {
    const total = suivi.referents.length;
    const repondu = suivi.referents.filter((r) => r.statut === "termine").length;
    if (total === 0) {
      message = "Aucun référent invité : pas de matching possible.";
      bloquant = true;
    } else if (suivi.versionCourante === null) {
      message = `${pluriel(total, "référent")} ${total > 1 ? "invités" : "invité"}, aucune réponse : pas encore de matching.`;
      bloquant = true;
    } else if (repondu < total) {
      message = `${repondu} référent${repondu > 1 ? "s" : ""} sur ${total} ${repondu > 1 ? "ont" : "a"} répondu : le matching peut encore évoluer.`;
    }
  }

  if (!message) return null;

  return (
    <div
      role="status"
      className={`flex flex-wrap items-center gap-3 rounded-surface border px-4 py-2.5 text-sm ${
        bloquant
          ? "border-etat-alerte-trait bg-etat-alerte-surface text-etat-alerte-contenu"
          : "border-etat-info-trait bg-etat-info-surface text-etat-info-contenu"
      }`}
    >
      <Compass className="h-4 w-4 shrink-0" />
      <span className="font-medium">{LIBELLE_CIBLE[cible]}</span>
      <span className="flex-1">{message}</span>
      <Button variant="outline" size="sm" onClick={onGerer}>
        Gérer
      </Button>
    </div>
  );
}
